import { BaseAssetManager, type AssetAction, type AssetChange } from '@gotti/core';
import { makeClientAssetsFacade } from './assetsFacade.js';

/** A manager that can also emit change notifications (duck-typed — see below). */
type ChangeEmittingAssetManager = BaseAssetManager & { onChange: (cb: (a: AssetAction, c: AssetChange) => void) => () => void };

function hasOnChange(mgr: BaseAssetManager): mgr is ChangeEmittingAssetManager {
  return typeof (mgr as Partial<ChangeEmittingAssetManager>).onChange === 'function';
}

/**
 * Attach `$assets` to a single client system + wire this system's asset hooks
 * to the shared per-process manager. Returns the unsubscribe for teardown.
 *
 * Mirrors `attachServerAssets` (`packages/server/src/assets/inject.ts`). The
 * plain `BaseAssetManager` used client-side (over `HttpAssetSource`) now owns
 * a live `onChange` emitter (Task 3), so the duck-type guard below activates
 * in practice — it stays in place defensively so this keeps working unchanged
 * if a future client manager is ever constructed without one.
 */
export function attachClientAssets(system: any, mgr: BaseAssetManager): () => void {
  // Defined once per instance (non-writable, non-configurable): a rolled-back
  // hot reload re-attaches the previous instance, which already has it. (A
  // fresh instance may carry a plain, configurable `$assets` class field.)
  if (Object.getOwnPropertyDescriptor(system, '$assets')?.configurable !== false) {
    Object.defineProperty(system, '$assets', { value: makeClientAssetsFacade(mgr), writable: false });
  }
  if (!hasOnChange(mgr)) return () => undefined;
  return mgr.onChange((action: AssetAction, change: AssetChange) => {
    if (action === 'updated') system.onAssetUpdated?.(change);
    else system.onAssetRemoved?.(change);
  });
}
